import moment from "moment";
import PropTypes from "prop-types";
import { LANGUAGES } from "../../constants/languages";
import { EMPLOYEES } from "../../constants/employees";
import Sphere from "../../icons/sphere.svg";
import Icon from "../Icon/Icon";
import getDecodeDescription from "../../services/decodeText";
import { getSpotLink } from "../../services/linkGenerator";
import styles from "./Company.module.scss";

const screeningClass = {
  "we have": styles.labelHave,
  "pre-approve": styles.labelApprove,
  "pre-reject": styles.labelReject,
};

const officesOf = (company) => {
  const listed = Array.isArray(company?.addresses)
    ? company.addresses.filter(Boolean)
    : [];
  const source = listed.length
    ? listed
    : [
        {
          country: company?.country,
          state: company?.state,
          city: company?.city,
          line1: company?.address,
          phone: company?.phone,
          email: company?.email,
          primary: true,
        },
      ];
  const offices = source.filter(
    (office) =>
      office.country ||
      office.state ||
      office.city ||
      office.line1 ||
      office.line2 ||
      office.postcode ||
      office.phone ||
      office.email
  );
  const headquarters = offices.find((office) => office.primary) || offices[0];
  if (!headquarters) return [];
  return [headquarters, ...offices.filter((office) => office !== headquarters)];
};

const Company = ({ company }) => {
  const renderLabels = (labels, title) => {
    return (
      <>
        <p className={styles.infoBlock}>
          <span className={styles.companyInfoTitle}>{title}</span>
        </p>

        <ul>
          {labels &&
            labels.map((label, index) => (
              <li className={styles.searchResultField} key={`${label}${index}`}>
                <span className={styles.field}>{label}</span>
              </li>
            ))}
        </ul>
      </>
    );
  };

  const getLabelsByValue = (collection, value) => {
    const labels = collection
      .map((item) => {
        if (value.find((languageValue) => item.value === languageValue))
          return item.label;
      })
      .filter(Boolean);

    return labels;
  };

  return (
    <div className={styles.company}>
      {company.screeningLabel && (
        <div className={styles.screening}>
          <span
            className={`${styles.screeningLabel} ${
              screeningClass[company.screeningLabel] || ""
            }`}
          >
            {company.screeningLabel}
          </span>
          {company.screeningLabel === "we have" && company.screeningMatchKey && (
            <a
              className={styles.screeningLink}
              href={getSpotLink(
                company.screeningMatchType,
                company.screeningMatchName,
                company.screeningMatchKey
              )}
              target="_blank"
              rel="noreferrer"
            >
              Open listing
            </a>
          )}
        </div>
      )}
      {company.noChanges && (
        <span className={styles.noChanges}>
          It is forbidden to do changes to this organization. Contact your
          manager if you have to make changes to this company.Заборонено вносити
          зміни в цю організацію. Зверніться до свого менеджера, якщо вам
          необхідно внести зміни в цю компанію{" "}
        </span>
      )}
      {company.logo && (
        <img className={styles.logo} src={company.logo} alt="logo" />
      )}
      <h5 className={styles.companyType}>{company.type}</h5>
      <h4 className={styles.companyName}>{company && company.name}</h4>
      <span className={styles.infoBlock}>
        <span className={styles.companyInfoTitle}>Description</span>
        <br />

        {company && company.description && (
          <pre className={styles.companyInfo}>
            {getDecodeDescription(company.description)}
          </pre>
        )}
      </span>
      {company?.fields && renderLabels(company.fields, "Fields")}
      {company?.category && renderLabels(company?.category, "Categories")}
      {officesOf(company).map((office, index) => {
        const street = [office.line1, office.line2, office.postcode]
          .filter(Boolean)
          .join(", ");
        const phone = office.phone || (office.primary ? company.phone : "");
        const email = office.email || (office.primary ? company.email : "");
        return (
          <div className={styles.infoBlock} key={`${office.line1 || "office"}-${index}`}>
            <span className={styles.companyInfoTitle}>
              {office.primary ? "Headquarters" : "Office"}
            </span>
            <br />
            {office.country && (
              <span className={styles.companyInfo}>Country: {office.country}</span>
            )}
            {office.country && <br />}
            {office.state && (
              <span className={styles.companyInfo}>State: {office.state}</span>
            )}
            {office.state && <br />}
            {office.city && (
              <span className={styles.companyInfo}>City: {office.city}</span>
            )}
            {office.city && <br />}
            {street && <span className={styles.companyInfo}>{street}</span>}
            {street && <br />}
            {phone && <span className={styles.companyInfo}>Phone: {phone}</span>}
            {phone && <br />}
            {email && <span className={styles.companyInfo}>Email: {email}</span>}
          </div>
        );
      })}

      <a className={styles.infoBlock} href={company?.link || ""} target="blank">
        {company && (
          <>
            <Icon name={Sphere} className={styles.organizationInfoIcon} />
            {company.link}
          </>
        )}
      </a>
      <p className={styles.infoBlock}>
        <span className={styles.companyInfoTitle}>Social media</span>
        <br />
        <span className={styles.companyInfo}>
          Facebook:{" "}
          <a href={company?.socialMedia?.facebook} target="blank">
            {" "}
            {company?.socialMedia?.facebook}
          </a>
        </span>
        <br />
        <span className={styles.companyInfo}>
          Linkedin:{" "}
          <a href={company?.socialMedia?.linkedin} target="blank">
            {company?.socialMedia?.linkedin}
          </a>
        </span>
      </p>
      <span className={styles.companyInfoTitle}>Languages</span>
      <ul>
        {getLabelsByValue(LANGUAGES, company?.languages).map(
          (language, index) => (
            <li key={`${language}${index}`}>
              <span>{language}</span>
            </li>
          )
        )}
      </ul>
      <p className={styles.infoBlock}>
        <span className={styles.companyInfoTitle}>Date of foundation</span>
        <br />
        <span className={styles.companyInfo}>{company?.foundationDate}</span>
      </p>
      <p className={styles.infoBlock}>
        <span className={styles.companyInfoTitle}> Number of employees</span>
        <br />
        <span className={styles.companyInfo}>
          {getLabelsByValue(EMPLOYEES, [company?.employeesNumber])}
        </span>
      </p>
      {company?.titleSeo && (
        <p className={styles.infoBlock}> Title SEO: {company.titleSeo}</p>
      )}
      {company?.descriptionSeo && (
        <p className={styles.infoBlock}>
          {" "}
          Description SEO: {company.descriptionSeo}
        </p>
      )}

      <p className={styles.infoBlock}>
        {moment(company.date).format("DD/MM/YYYY")}
      </p>
      <p className={styles.infoBlock}> Author: {company.author}</p>
      <p className={styles.infoBlock}> Comment: {company?.comment}</p>
    </div>
  );
};

Company.propTypes = {
  company: PropTypes.object,
};

export default Company;
